import { defineStore } from 'pinia'
import authService from '@/services/auth-service'

export const useOAuthStore = defineStore('OAuth', {
  state: () => ({
    status: {
      isAuthorized: false,
      hasAuthenticators: false,
      hasToken: false,
    },
    user: null,
  }),

  getters: {
    isAuthorized(): boolean {
      return authService.hasAccessToken()
    },
    hasAuthenticators(state): boolean {
      return state.status.hasAuthenticators
    },
  },

  actions: {
    authorizeSuccess(user: any) {
      this.status.isAuthorized = true
      this.user = user
    },

    authenticatorsSuccess() {
      this.status.hasAuthenticators = true
    },

    tokenSuccess() {
      this.status.hasToken = true
    },

    loginFailure() {
      this.status.isAuthorized = false
      this.user = null
    },

    logout() {
      this.status.isAuthorized = false
      this.user = null
    },

    registerSuccess() {
      this.status.isAuthorized = false
    },

    registerFailure() {
      this.status.isAuthorized = false
    },

    async authorize(data: any) {
      try {
        const user = await authService.authorize({
          username: data.username.toLowerCase(),
          password: data.password,
        })
        this.authorizeSuccess(user)
        return Promise.resolve(user)
      } catch (error) {
        console.debug(error)
        this.loginFailure()
        return Promise.reject(error)
      }
    },

    async authenticators() {
      try {
        await authService.authenticators()
        this.authenticatorsSuccess()
      } catch (error: any) {
        this.loginFailure()
        const message = error.response?.data?.message || error.message || error.toString()
        return Promise.reject(message)
      }
    },

    async token(bindingCode?: any) {
      try {
        await authService.token(bindingCode)
        this.tokenSuccess()
      } catch (error: any) {
        this.loginFailure()
        const message = error.response?.data?.message || error.message || error.toString()
        return Promise.reject(message)
      }
    },

    async associate(mobileNumber: any) {
      try {
        return await authService.associate(mobileNumber)
      } catch (error: any) {
        this.loginFailure()
        const message = error.response?.data?.message || error.message || error.toString()
        return Promise.reject(message)
      }
    },

    async signOut() {
      try {
        await authService.logout()
        this.logout()
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async forgot(email: any) {
      try {
        await authService.forgot(email)
        this.logout()
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async register(data: any) {
      try {
        const response = await authService.register({
          username: data.email,
          password: data.password,
          passwordCheck: data.passwordCheck,
        })
        this.registerSuccess()
        return Promise.resolve(response.data)
      } catch (error: any) {
        this.registerFailure()
        const message = error.response?.data?.message || error.message || error.toString()
        return Promise.reject(message)
      }
    },
  },
})
